/**
 * FEATURE FLAGS
 * Controls whether an entire feature surface is exposed to users.
 * Separate from dataSourceFlags (which controls mock vs. live data routing).
 *
 * CONVENTION: Flags auto-enable in `import.meta.env.DEV` so contributors don't
 * need local env setup for in-progress features. Non-dev builds (staging, production)
 * require an explicit `VITE_FEATURE_{NAME}=true` opt-in.
 * This mirrors the existing pattern in dataSourceFlags.useLiveGoals.
 */

const truthyValues = new Set(['1', 'true', 'yes', 'on']);

/**
 * Normalizes an environment variable value into a boolean.
 */
const isEnabled = (envValue: string | undefined): boolean => truthyValues.has((envValue ?? '').toLowerCase());

export const featureFlags = {
  /**
   * Goals feature — full UI surface including nav, routes, and discovery.
   * WHY: Under active development, not ready for production release.
   * Auto-enabled in dev mode. Requires VITE_FEATURE_GOALS=true in staging/production.
   */
  get goals() {
    // CONSTRAINT: Explicit 'false' always wins, even in dev — allows local opt-out.
    if (import.meta.env.VITE_FEATURE_GOALS === 'false') return false;
    // WHY: Auto-enable in dev so contributors don't need env setup for in-progress features.
    if (import.meta.env.DEV) return true;
    return isEnabled(import.meta.env.VITE_FEATURE_GOALS);
  }
} as const;
